import { Component, OnInit, ChangeDetectionStrategy, OnDestroy, ChangeDetectorRef } from "@angular/core";
import { Contract } from "@edp-components/edp-contract";
import { EdpModalService } from "@edp-components/edp-modal";
import { EdpToastType } from "@edp-components/edp-toaster/edp-toast-type";
import { EdpToasterService } from "@edp-components/edp-toaster/edp-toaster.service";
import { BusinessAgrementCollection, BusinessAgrementConsent, BusinessAgrementStatusAPI } from "@edp-models/business-agrements.model";
import { GetBusinessAgrements, UdpateBusinessAgrements, UpdateBusinessAgrementsStatus } from "@edp-store/actions/contract.actions";
import { ShowLoader } from "@edp-store/actions/layout.actions";
import { getBusinessAgrements, getContract, getUpdateBusinessAgrementsStatus } from "@edp-store/selectors/contract.selectors";
import { IAppState } from "@edp-store/state/app.state";
import { sleep } from "@edp-utils/sleep.utils";
import { select, Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { Subject } from "rxjs";
import { filter, take, takeUntil } from "rxjs/operators";

@Component({
    selector: "edp-communications-consents-modal",
    templateUrl: "./edp-communications-consents-modal.component.html",
    styleUrls: ["./edp-communications-consents-modal.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EdpCommunicationsConsentsModalComponent implements OnInit, OnDestroy {
    private unsubscribe = new Subject();
    private selectedContract: Contract;
    public generalConsents: BusinessAgrementConsent[] = [
        {
            Date: "",
            Id: "CONS_6",
            Type: "",
            Value: false,
            expand: false,
        },
        {
            Date: "",
            Id: "CONS_2",
            Type: "",
            Value: false,
            expand: false,
        },
        {
            Date: "",
            Id: "CONS_1",
            Type: "",
            Value: false,
        },
        {
            Date: "",
            Id: "CONS_4",
            Type: "",
            Value: false,
            expand: false,
        },
        {
            Date: "",
            Id: "CONS_5",
            Type: "",
            Value: false,
            expand: false,
        },
        {
            Date: "",
            Id: "CONS_3",
            Type: "",
            Value: false,
        },
        {
            Date: "",
            Id: "CONS_7",
            Type: "",
            Value: false,
            readonly: true,
        },
    ];
    private businessAgrements: BusinessAgrementCollection;
    public selectedAll = false;

    constructor(
        private edpModalService: EdpModalService,
        private store: Store<IAppState>,
        private readonly toasterService: EdpToasterService,
        private readonly translateService: TranslateService,
        private cdr: ChangeDetectorRef
    ) {
        this.store.pipe(takeUntil(this.unsubscribe), select(getContract)).subscribe((contract: Contract) => {
            if (!!contract) {
                this.selectedContract = contract;
                this.store.dispatch(new ShowLoader(true));
                this.store.dispatch(new GetBusinessAgrements({ contractId: this.selectedContract.ContractId }));
            }
        });

        this.store
            .pipe(takeUntil(this.unsubscribe), select(getBusinessAgrements))
            .subscribe((businessAgrements: BusinessAgrementCollection) => {
                if (businessAgrements) {
                    this.businessAgrements = businessAgrements;
                    this.store.dispatch(new ShowLoader(false));
                    this.setConsents();
                }
            });
    }

    ngOnInit(): void {}

    public ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }

    public close() {
        this.edpModalService.close(true);
    }

    private filterConsents(element: BusinessAgrementConsent): boolean {
        return element.Id !== "CONS_1" && element.Id !== "CONS_3";
    }

    private convertValueConsent(element: BusinessAgrementConsent): boolean {
        return element.Value === "S";
    }

    private setSelectedAll() {
        this.selectedAll = this.generalConsents.filter(el => this.filterConsents(el)).every(el => !!el.Value);
    }

    private setConsents() {
        this.generalConsents = this.generalConsents
            .map(el => {
                const consent = this.businessAgrements.Customer.CONS_LIST.find(el2 => el2.Id === el.Id);
                if (consent?.Value !== "S" && consent?.Value !== "N" && consent?.Value !== "V" && consent?.Value !== null) {
                    return { Id: null };
                }
                return {
                    ...el,
                    ...consent,
                    ...{ Value: this.filterConsents(el) ? this.convertValueConsent(consent) : consent.Value },
                };
            })
            .filter(el => el.Id !== "CONS_7")
            .filter(el => el.Id !== null);
        this.setSelectedAll();

        this.cdr.detectChanges();
    }

    private showToast(text, type: EdpToastType): void {
        this.toasterService
            .create(text, type, {
                timeout: 3000,
            })
            .show();
    }

    private handleSubmitStatus(): void {
        this.store
            .pipe(
                select(getUpdateBusinessAgrementsStatus),
                filter(i => !!i),
                take(1)
            )
            .subscribe((status: BusinessAgrementStatusAPI) => {
                this.store.dispatch(new UpdateBusinessAgrementsStatus(null));
                if (status?.status === false) {
                    this.showToast(
                        this.translateService.instant("pt.edp.edponline.communications.consents.submit.error"),
                        EdpToastType.error
                    );
                }

                if (status?.status === true) {
                    this.showToast(this.translateService.instant("pt.edp.edponline.communications.consents.success"), EdpToastType.success);
                }
            });
    }

    public submit() {
        const newConsents = [];

        this.generalConsents.forEach(el => {
            newConsents.push({
                ID: el.Id,
                Value: this.filterConsents(el) ? (!!el.Value ? "S" : "N") : el.Value,
            });
        });

        this.store.dispatch(
            new UdpateBusinessAgrements({
                contractId: this.selectedContract.ContractId,
                Attributes: newConsents,
            })
        );
        this.handleSubmitStatus();
    }

    public setOption(event, consentId) {
        const consent = this.generalConsents.find(el => el.Id === consentId);

        if (!!consent.readonly && consent.Value !== event) {
            consent.Value = event;
            this.showToast(
                this.translateService.instant("pt.edp.edponline.communications.consents.notpermitededit.CONS_7"),
                EdpToastType.warning
            );
            sleep(300).then(() => {
                consent.Value = !consent.Value;
                this.cdr.detectChanges();
            });
        }

        if (consent) {
            consent.Value = !consent.Value;
        }

        this.setSelectedAll();
    }

    public setAll(event) {
        this.selectedAll = event;
        this.generalConsents
            .filter(el => !el.readonly)
            .map(el => {
                el.Value = this.filterConsents(el) ? event : el.Value;
                return el;
            });
    }

    public expand(consentId) {
        const consent = this.generalConsents.find(el => el.Id === consentId);
        consent.expand = !consent.expand;
    }
}
